import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DimensionMap } from "models/propTypes/dimensionPropTypes";
import { UtilFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
//#endregion
import * as Yup from "yup";
import {
  DateHelper,
  DimensionHelper,
  LocalizationHelperInstance,
  MathHelper,
  UtilsHelper,
  PhoneHelper,
} from "utils/helpers";

const CustomFormValidations: React.FC = () => {
  React.useEffect(() => {
    addCustomValidations();
  }, []);

  const addCustomValidations = () => {
    Yup.addMethod(
      Yup.array,
      "checkForcedDimensions",
      function (
        forcedDimensionMap: {
          type: WintSuperkollAPIModel.DimensionType;
          dimensionIds: string[];
        }[]
      ) {
        return this.test(
          "checkForcedDimensions",
          "",
          function (this: Yup.TestContext, value?: WintSuperkollAPIModel.DimensionData[] | string[]) {
            const { path, createError } = this;
            return new Promise<boolean | Yup.ValidationError>((resolve) => {
              const requiredMissing = forcedDimensionMap
                .map((fm) => ({
                  name: fm.type.name,
                  type: fm.type.type,
                  contains:
                    value &&
                    value.length > 0 &&
                    (typeof value[0] === "string"
                      ? (value as string[]).find((v: string) => fm.dimensionIds.some((d) => d === v)) !== undefined
                      : (value as WintSuperkollAPIModel.DimensionData[]).find(
                          (v: WintSuperkollAPIModel.DimensionData) => fm.dimensionIds.some((d) => d === v.dimensionId)
                        ) !== undefined),
                }))
                .filter((x) => !x.contains)
                .map((f) => DimensionHelper.getDimensionTypeText(f.type, f.name));

              resolve(
                requiredMissing.length === 0 ||
                  createError({
                    path,
                    message: LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Validations.MissingRequiredDimensionType,
                      "",
                      {
                        types: requiredMissing.join(", "),
                      }
                    ),
                  })
              );
            });
          }
        );
      }
    );

    Yup.addMethod(Yup.array, "validateDimensionAmounts", function (dimensionMap: DimensionMap, amount: number) {
      return this.test(
        "validateDimensionAmounts",
        "",
        function (this: Yup.TestContext, value?: WintSuperkollAPIModel.DimensionData[]) {
          const { path, createError } = this;
          return new Promise<boolean | Yup.ValidationError>((resolve) => {
            const totalInvalid = dimensionMap
              .map((dm) => ({
                name: dm.key.name,
                type: dm.key.type,
                val: value
                  ? ///due to precision issues in JS:
                    ///https://stackoverflow.com/questions/1458633/how-to-deal-with-floating-point-number-precision-in-javascript
                    JSON.parse(
                      MathHelper.toFixedNoRound(
                        value
                          .filter((v) => dm.value.some((d) => d.id === v.dimensionId))
                          .map((dim) => Number(dim.amount ?? 0))
                          .reduce((tot, val) => tot + val, 0),
                        2
                      ).toString()
                    )
                  : 0,
              }))
              .filter((x) => (amount >= 0 && x.val > amount) || (amount < 0 && x.val < amount))
              .map((f) => DimensionHelper.getDimensionTypeText(f.type, f.name));

            resolve(
              totalInvalid.length === 0 ||
                createError({
                  path,
                  message: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Validations.InvalidAmountDimensionType,
                    "",
                    {
                      types: totalInvalid.join(", "),
                    }
                  ),
                })
            );
          });
        }
      );
    });

    Yup.addMethod(Yup.object, "validateMonthRange", function (errorMessage?: string) {
      return this.test(
        "validateMonthRange",
        errorMessage ?? "",
        function (this: Yup.TestContext, value?: UtilFormTypes.DateRange) {
          const { path, createError } = this;
          return new Promise<boolean | Yup.ValidationError>((resolve) => {
            const startMonth = value?.start && DateHelper.getYearMonth(value.start);
            const endMonth = value?.end && DateHelper.getYearMonth(value.end);
            let errorPath = path;
            let valid;
            let message = errorMessage ? LocalizationHelperInstance.getFormattedMessage(errorMessage) : "";

            if (startMonth && !endMonth) {
              errorPath = `${path ?? ""}.end`;
              valid = false;
              message = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Required);
            } else if (!startMonth && endMonth) {
              errorPath = `${path ?? ""}.start`;
              valid = false;
              message = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Required);
            } else {
              valid = (!startMonth && !endMonth) || (startMonth && endMonth && startMonth <= endMonth);
              message = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.InvalidMonthRange);
            }

            resolve(
              valid ||
                createError({
                  path: errorPath,
                  message: message,
                })
            );
          });
        }
      );
    });

    Yup.addMethod(Yup.string, "validatePhone", function phoneValidation(this: Yup.StringSchema, msg: string) {
      return this.test("validatePhone", msg, function (this: Yup.TestContext, phoneToValidate?: string) {
        const { path, createError } = this;
        const isValidPhoneFuncion: (phoneToValidate?: string) => boolean = function (value) {
          if (value && !PhoneHelper.validate(value)) return false;

          return true;
        };

        return (
          isValidPhoneFuncion(phoneToValidate) ||
          createError({
            path,
            message: msg,
          })
        );
      });
    });

    Yup.addMethod(Yup.string, "multipleEmails", function invalidEmails(this: Yup.StringSchema, msg: string) {
      return this.test("multipleEmails", msg, function (this: Yup.TestContext, value?: string) {
        const { path, createError } = this;
        const emails = value ? value.replace(/\s/g, "").split(",") : [];
        const emailValidations: boolean[] = emails.map((email: any) => {
          const schema = Yup.string().email(TranslationKeys.Validations.InvalidEmail);
          return schema.isValidSync(email);
        });

        return (
          emailValidations.find((v) => !v) === undefined ||
          createError({
            path,
            message: msg,
          })
        );
      });
    });

    Yup.addMethod(
      Yup.array,
      "unique",
      function uniqueArray(this: ArraySchema<any>, msg: string, comparisonKey?: string) {
        return this.test("unique", msg, function (this: Yup.TestContext, values?: any[]) {
          const { path, createError } = this;

          const notEmpty = values?.filter((v) => !UtilsHelper.isEmpty(v));

          return (
            notEmpty === undefined ||
            new Set(notEmpty.map((val) => (comparisonKey ? val[comparisonKey] : JSON.stringify(val)))).size ===
              notEmpty.length ||
            createError({
              path,
              message: msg,
            })
          );
        });
      }
    );

    Yup.addMethod(
      Yup.array,
      "uniqueProperties",
      function (
        mapper = (a: any) => a,
        // eslint-disable-next-line no-template-curly-in-string --- disabled to avoid warning and regression testing if changed
        message: string = "${path} " +
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.NoDuplicates)
      ) {
        return this.test("unique", message, function (this: Yup.TestContext, list?: any[]) {
          const { path, createError } = this;
          return (
            !list ||
            list.length === new Set(list.map(mapper)).size ||
            createError({
              path: `${path}`,
              message: message,
            })
          );
        });
      }
    );

    Yup.addMethod(Yup.object, "validateNumberRange", function (errorMessage?: string) {
      return this.test(
        "validateNumberRange",
        errorMessage ?? "",
        function (this: Yup.TestContext, value?: UtilFormTypes.NumberRange) {
          const { path, createError } = this;
          return new Promise<boolean | Yup.ValidationError>((resolve) => {
            let isValid = true;
            if (value && value.lowest && value.highest && value.lowest > value.highest) {
              isValid = false;
            }

            resolve(
              isValid ||
                createError({
                  path,
                  message: LocalizationHelperInstance.getFormattedMessage(
                    errorMessage ?? TranslationKeys.Validations.InvalidNumberRange
                  ),
                })
            );
          });
        }
      );
    });
  };

  return <></>;
};

export default CustomFormValidations;
